import React, { useMemo } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Drawer, type MenuProps, Tag, Typography, Divider } from 'antd';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { ChatIconButton } from './chat/ChatIconButton';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useResponsive } from '@municipality/ui';
import type { NavigationItem, NavItem } from '../types';

const { Text } = Typography;

const { Header, Sider, Content } = Layout;

const StyledHeader = styled(Header, {
  shouldForwardProp: (prop) => prop !== '$isTablet',
})<{ $isTablet: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding-left: 5px;
  padding-right: 16px;
  gap: 0;
  height: 64px;
  max-width: 100%;
  overflow: hidden;
  
  ${({ $isTablet }) =>
    $isTablet &&
    `
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  `}
`;

// Animation keyframes
const fadeInMenu = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled components
const StyledSider = styled(Sider)`
  &.ant-layout-sider {
    background: #fafafa !important;
    border-right: 1px solid #f0f0f0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    position: relative !important;
    z-index: 100 !important;
    flex: 0 0 256px !important;
    max-width: 256px !important;
    min-width: 256px !important;
    width: 256px !important;
  }
  
  &.ant-layout-sider-collapsed {
    flex: 0 0 80px !important;
    max-width: 80px !important;
    min-width: 80px !important;
    width: 80px !important;
  }
  
  .ant-layout-sider-children {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden !important;
  }
  
  /* Force menu items to hide text when collapsed but keep icons visible */
  &.ant-layout-sider-collapsed .ant-menu-item {
    padding-left: 24px !important;
  }
  
  &.ant-layout-sider-collapsed .ant-menu-item .ant-menu-title-content {
    opacity: 0 !important;
  }
  
  &.ant-layout-sider-collapsed .ant-menu-submenu-title .ant-menu-title-content {
    opacity: 0 !important;
  }
  
  /* Ensure icons remain visible when collapsed */
  &.ant-layout-sider-collapsed .ant-menu-item .anticon {
    opacity: 1 !important;
    color: inherit !important;
  }
  
  &.ant-layout-sider-collapsed .ant-menu-submenu-title .anticon {
    opacity: 1 !important;
    color: inherit !important;
  }
`;

const CustomToggleButton = styled.button`
  width: 64px;
  height: 64px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
  
  &:hover {
    background-color: transparent;
    color: #666;
  }
  
  &:active {
    background-color: transparent;
    color: #666;
  }
  
  &:focus {
    outline: none;
    background-color: transparent;
    color: #666;
  }
`;

const AnimatedMenu = styled(Menu)`
  .ant-menu-item {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    animation: ${fadeInMenu} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .ant-menu-item:hover {
    transform: translateX(4px);
  }
  
  .ant-menu-item-selected {
    transform: translateX(4px);
  }
`;

// Menu without shadow for user dropdown
const UserDropdownMenu = styled(Menu)`
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  
  .ant-menu-item {
    box-shadow: none !important;
  }
  
  .ant-menu-item:hover {
    box-shadow: none !important;
  }
`;

// Layout wrappers
const MainLayout = styled(Layout)`
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
`;

const ContentLayout = styled(Layout, {
  shouldForwardProp: (prop) => prop !== '$shouldShowBurgerMenu',
})<{ $shouldShowBurgerMenu: boolean }>`
  width: ${({ $shouldShowBurgerMenu }) => ($shouldShowBurgerMenu ? '100%' : 'auto')};
  margin-left: ${({ $shouldShowBurgerMenu }) => ($shouldShowBurgerMenu ? '0' : 'auto')};
  max-width: 100%;
  overflow: hidden;
`;

// Header right section container
const HeaderRightSection = styled.div<{ $isMobile: boolean; $shouldShowBurgerMenu: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isMobile }) => ($isMobile ? '11px' : '16px')};
  flex-shrink: 0;
  margin-left: auto;
  max-width: ${({ $shouldShowBurgerMenu }) => ($shouldShowBurgerMenu ? 'calc(100% - 60px)' : 'auto')};
`;

// Language switcher button with CSS hover
const LanguageButton = styled(Button)`
  transition: transform 0.2s ease, background-color 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    background-color: #f0f0f0;
  }
`;

// User profile button with CSS hover
const UserProfileButton = styled(Button)`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: visible;
  padding: 20px 4px;
  transition: transform 0.2s ease, background-color 0.2s ease;
  border-radius: 20px;
  background-color: #EDEDED;
  border: solid 1px #dbdbdb;
  
  &:hover {
    transform: scale(0.98);
    background-color: #E0E0E0;
  }
`;

// Avatar wrapper with CSS hover
const AvatarWrapper = styled.div`
  display: inline-block;
  transition: transform 0.2s ease, filter 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.1);
    filter: drop-shadow(0 2px 8px rgba(24, 144, 255, 0.4));
  }
`;

// Styled Avatar
const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
  background-color: #1890ff;
  color: #ffffff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
`;

// Display name span
const DisplayNameSpan = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
  margin-left: 0;
`;

// Mobile burger button (extends CustomToggleButton)
const MobileBurgerButton = styled(CustomToggleButton)`
  min-width: 44px;
  min-height: 44px;
  background-color: transparent;
  border: none;
  color: #666;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// User dropdown content styled components
const UserDropdownContainer = styled.div`
  min-width: 240px;
  padding: 8px 0 0 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
`;

const UserInfoSection = styled.div<{ $padding?: string }>`
  padding: ${({ $padding }) => $padding || '12px 16px'};
  padding-bottom: 0px;
`;

const UserFullNameText = styled(Text)`
  font-size: 16px;
  line-height: 1.5;
`;

const UserEmailText = styled(Text)`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
`;

const UserRoleSection = styled.div`
  padding: 0px 16px;
`;

const UserDropdownDivider = styled(Divider)`
  margin: 8px 0;
`;

// Content area styled components
const StyledContent = styled(Content, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? '10px 0px' : '20px')};
  background: #ffffff;
  max-width: 100%;
  overflow: visible;
`;

const ContentInner = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? '10px' : '30px')};
  background: #ffffff;
  min-height: calc(100vh - 160px);
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;
`;

// Drawer styled components
const DrawerTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DrawerContent = styled.div`
  padding: 16px 0;
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
  }
  .ant-drawer-header {
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
  }
`;

// Borderless menu
const BorderlessMenu = styled(Menu)`
  border: none;
`;

const LogoContainer = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f0f0f0;
`;

const LogoWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img<{ $collapsed: boolean }>`
  object-fit: contain;
  height: ${({ $collapsed }) => ($collapsed ? '32px' : '30px')};
  width: ${({ $collapsed }) => ($collapsed ? '32px' : 'auto')};
  max-width: ${({ $collapsed }) => ($collapsed ? '32px' : '200px')};
`;

// Simple Logo component using styled-components
const SimpleLogo: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const fullLogoUrl = '/assets/logos/dimotis-full-logo.svg';
  const smallLogoUrl = '/assets/logos/dimotis-small-logo-png.png';
  
  return (
    <LogoWrapper>
      <LogoImage 
        src={collapsed ? smallLogoUrl : fullLogoUrl} 
        alt="Dimotis Logo" 
        $collapsed={collapsed}
      />
    </LogoWrapper>
  );
};

interface BreadcrumbItem {
  title: string;
  path?: string;
}

interface SuperAdminLayoutComponentProps {
  children: React.ReactNode;
  user?: {
    displayName?: string;
    email?: string;
    name?: string;
    surname?: string;
  };
  navItems: NavigationItem[];
  breadcrumbItems: BreadcrumbItem[];
  onLogout: () => void;
  onNavigate: (path: string) => void;
  onLanguageChange?: (language: string) => void;
  showLanguageSwitcher?: boolean;
}

export const SuperAdminLayoutComponent: React.FC<SuperAdminLayoutComponentProps> = ({
  children,
  user,
  navItems,
  breadcrumbItems: _breadcrumbItems,
  onLogout,
  onNavigate,
  onLanguageChange,
  showLanguageSwitcher,
}) => {
  const { t } = useTranslation('common');
  const { isMobile, isTablet } = useResponsive();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = React.useState(false);
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);

  // Get user initials from displayName
  const getUserInitials = (displayName?: string): string => {
    if (!displayName) return 'SA';
    const nameParts = displayName.trim().split(' ');
    if (nameParts.length === 0) return 'SA';
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    // Get first letter of first name and first letter of last name
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return (firstInitial + lastInitial) || 'SA';
  };

  // Format display name: Full first name + Last name initial with dot
  // Example: "Marco Polo" → "Marco P."
  const formatDisplayName = (displayName?: string): string => {
    if (!displayName) return 'Super Admin';
    const nameParts = displayName.trim().split(' ').filter(part => part.length > 0);
    if (nameParts.length === 0) return 'Super Admin';
    if (nameParts.length === 1) return nameParts[0];
    // First name (full) + Last name (first letter + dot)
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    const lastNameInitial = lastName.charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  // Show sidebar on desktop, burger menu on mobile/tablet
  const shouldShowSidebar = !isMobile && !isTablet;
  const shouldShowBurgerMenu = isMobile || isTablet;

  // Close all submenus when sidebar collapses
  React.useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    }
  }, [collapsed]);

  // Handle submenu active states
  const getSelectedKeys = (pathname: string): string[] => {
    return [pathname];
  };

  const handleMenuClick = React.useCallback(({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      onNavigate(key);
      // Close mobile drawer after navigation
      if (shouldShowBurgerMenu) {
        setMobileDrawerVisible(false);
      }
    } else if (key === 'logout') {
      onLogout();
    }
  }, [onNavigate, onLogout, shouldShowBurgerMenu]);

  const handleOpenChange = (newOpenKeys: string[]) => {
    setOpenKeys(newOpenKeys);
  };

  const handleLanguageChange = ({ key }: { key: string }) => {
    if (onLanguageChange) {
      onLanguageChange(key);
    }
  };

  // Get user's full name, email
  const getUserFullName = (): string => {
    if (user?.name && user?.surname) {
      return `${user.name} ${user.surname}`;
    }
    return user?.displayName || 'Super Admin';
  };

  const getUserEmail = (): string => {
    return user?.email || 'No email';
  };

  const userFullName = getUserFullName();
  const userEmail = getUserEmail();

  // Custom dropdown content with user info
  const userDropdownContent = useMemo(() => (
    <UserDropdownContainer>
      {/* Full Name - Bigger text */}
      <UserInfoSection $padding="12px 16px">
        <UserFullNameText strong>
          {userFullName}
        </UserFullNameText>
      </UserInfoSection>

      {/* Email - Same styling as menu items */}
      <UserInfoSection $padding="8px 16px">
        <UserEmailText>
          {userEmail}
        </UserEmailText>
      </UserInfoSection>

      {/* Role Tag - Super Admin */}
      <UserRoleSection>
        <Tag color="red">
          Super Admin
        </Tag>
      </UserRoleSection>

      <UserDropdownDivider />

      {/* Profile and Logout menu items */}
      <UserDropdownMenu
        items={[
          { key: '/profile', icon: <UserOutlined />, label: t('profile.title', 'Profile') },
          { type: 'divider' },
          { key: 'logout', icon: <LogoutOutlined />, label: t('logout.title', 'Logout'), onClick: onLogout },
        ]}
        onClick={handleMenuClick}
      />
    </UserDropdownContainer>
  ), [userFullName, userEmail, t, handleMenuClick, onLogout]);

  const languageMenuItems: MenuProps['items'] = [
    { key: "en", label: "English", onClick: () => handleLanguageChange({ key: "en" }) },
    { key: "el", label: "Ελληνικά", onClick: () => handleLanguageChange({ key: "el" }) }
  ];

  return (
    <MainLayout>
      {/* Desktop Sidebar - Hidden on mobile/tablet */}
      {shouldShowSidebar && (
        <StyledSider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={256}
          collapsedWidth={80}
          breakpoint="lg"
          onBreakpoint={() => {
            // Don't auto-collapse, let user control it manually
          }}
          key={`sidebar-${collapsed}`}
        >
        <LogoContainer>
          <SimpleLogo collapsed={collapsed} />
        </LogoContainer>
        <AnimatedMenu
          mode="inline"
          selectedKeys={getSelectedKeys(location.pathname)}
          openKeys={openKeys}
          items={navItems.map(item => {
            if ('type' in item && item.type === 'divider') {
              return { 
                type: 'divider'
              };
            }
            const navItem = item as NavItem;
            return {
              key: navItem.key,
              label: navItem.label,
              icon: navItem.icon,
              disabled: navItem.disabled,
              children: navItem.children?.map(child => ({
                key: child.key,
                label: child.label,
                icon: child.icon,
                disabled: child.disabled
              }))
            };
          }) as MenuProps['items']}
          onClick={handleMenuClick}
          onOpenChange={handleOpenChange}
        />
        </StyledSider>
      )}
      
      <ContentLayout $shouldShowBurgerMenu={shouldShowBurgerMenu}>
        <StyledHeader $isTablet={isTablet}>
          {/* Desktop sidebar toggle */}
          {shouldShowSidebar && (
            <CustomToggleButton
              onClick={() => {
                setCollapsed(!collapsed);
              }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </CustomToggleButton>
          )}
          
          {/* Mobile burger menu - Always show on mobile/tablet */}
          {shouldShowBurgerMenu && (
            <MobileBurgerButton
              onClick={() => {
                setMobileDrawerVisible(true);
              }}
            >
              <MenuOutlined />
            </MobileBurgerButton>
          )}
          
          <HeaderRightSection $isMobile={isMobile} $shouldShowBurgerMenu={shouldShowBurgerMenu}>
            {showLanguageSwitcher && (
              <Dropdown
                menu={{
                  items: languageMenuItems
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <LanguageButton 
                  shape="circle" 
                  icon={<GlobalOutlined />}
                />
              </Dropdown>
            )}

            <ChatIconButton />

            <Dropdown
              popupRender={() => userDropdownContent}
              placement="bottomRight"
              trigger={['click']}
            >
              <UserProfileButton type="text">
                <AvatarWrapper>
                  <StyledAvatar size={32}>
                    {getUserInitials(user?.displayName)}
                  </StyledAvatar>
                </AvatarWrapper>
                {!(isMobile || isTablet) && (
                  <DisplayNameSpan>
                    {formatDisplayName(user?.displayName)}
                  </DisplayNameSpan>
                )}
              </UserProfileButton>
            </Dropdown>
          </HeaderRightSection>
        </StyledHeader>
        
        <StyledContent $isMobile={isMobile}>
          <ContentInner $isMobile={isMobile}>
            {children}
          </ContentInner>
        </StyledContent>
      </ContentLayout>
      
      {/* Mobile Navigation Drawer */}
      {shouldShowBurgerMenu && (
        <StyledDrawer
          title={
            <DrawerTitleContainer>
              <SimpleLogo collapsed={false} />
            </DrawerTitleContainer>
          }
          placement="left"
          onClose={() => setMobileDrawerVisible(false)}
          open={mobileDrawerVisible}
          width={280}
        >
          <DrawerContent>
            {/* Main Navigation */}
            <BorderlessMenu
              mode="inline"
              selectedKeys={getSelectedKeys(location.pathname)}
              openKeys={openKeys}
              items={navItems.map(item => {
                if ('type' in item && item.type === 'divider') {
                  return { 
                    type: 'divider'
                  };
                }
                const navItem = item as NavItem;
                return {
                  key: navItem.key,
                  icon: navItem.icon,
                  label: navItem.label,
                  disabled: navItem.disabled,
                  children: navItem.children?.map(child => ({
                    key: child.key,
                    label: child.label,
                    icon: child.icon,
                    disabled: child.disabled
                  }))
                };
              }) as MenuProps['items']}
              onClick={handleMenuClick}
              onOpenChange={handleOpenChange}
            />
          </DrawerContent>
        </StyledDrawer>
      )}
    </MainLayout>
  );
};
